import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Logo from "../images/Logo.png";
import CoralReef from "../images/CoralReef.jpg";
import NetworkCanvas from "./NetworkCanvas";
import { preRegister, register, authenticateGoogle } from "../service/AuthService";
import { errorMessage } from "../service/HttpErrors";
import { getSignupAttribution, clearSignupAttribution } from "../service/Attribution";
import {ftAccessToken, ftRefreshToken, ftUserPhoto, ftUnreadCount} from "../constants/Constants";
import { properties } from "../properties";

// The backend won't send more than a few codes an hour to one address (it still
// answers success), so the copy must hold whether or not a new email went out
const RESENT = "If a new email doesn't arrive within a minute, use the newest code we've already sent you.";
const CODE_TTL_MS = 60 * 60 * 1000;

export default function Register() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get("invite");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [form, setForm] = useState({
        firstName: "", lastName: "", email: "", password: "", confirm: "",
    });

    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleReady, setGoogleReady] = useState(false);
    const [step, setStep] = useState("register"); // "register" | "confirm"
    const [confirmationCode, setConfirmationCode] = useState("");
    const [notice, setNotice] = useState("");
    // Where and when this page last had a code sent
    const [lastSent, setLastSent] = useState({ email: null, at: 0 });
    // One request at a time: stops double clicks and held Enter from resubmitting
    const inFlight = useRef(false);

    useEffect(() => {
        localStorage.removeItem(ftAccessToken);
        localStorage.removeItem(ftRefreshToken);
        localStorage.removeItem(ftUserPhoto);
        localStorage.removeItem(ftUnreadCount);
        let attempts = 0;
        const wait = () => {
            if (window.google?.accounts?.oauth2) { setGoogleReady(true); }
            else if (attempts++ < 100) { setTimeout(wait, 100); }
        };
        wait();
    }, []);

    const handleGoogleSignIn = () => {
        if (!window.google?.accounts?.oauth2) return;
        /* global google */
        google.accounts.oauth2.initCodeClient({
            client_id: properties.google.clientId,
            scope: "openid email profile",
            ux_mode: "popup",
            callback: (response) => {
                setLoading(true);
                authenticateGoogle({ ...response, attribution: getSignupAttribution() })
                    .then(res => {
                        if (res === 500) { setError("Google sign-up failed."); return; }
                        if (res.new) clearSignupAttribution();
                        localStorage.setItem(ftAccessToken, JSON.stringify(res.authenticationResponse.access_token));
                        localStorage.setItem(ftRefreshToken, JSON.stringify(res.authenticationResponse.refresh_token));
                        navigate(res.new ? "/onboard" : "/");
                    })
                    .catch((err) => setError(errorMessage(err, "Google sign-up failed.")))
                    .finally(() => setLoading(false));
            },
        }).requestCode();
    };

    const handleChange = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); setError(""); };

    const handleRegister = async () => {
        if (!form.firstName || !form.lastName || !form.email || !form.password || !form.confirm) {
            setError("Please fill in all fields.");
            return;
        }
        if (!agreed) {
            setError("You must agree to the Terms of Service and Privacy Policy.");
            return;
        }
        if (form.password !== form.confirm) {
            setError("Passwords do not match.");
            return;
        }
        if (form.password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        // Coming back from the code step for the same address: the code already
        // emailed still works, so don't send (and wait for) another one
        if (sameAddress(lastSent.email, form.email) && Date.now() - lastSent.at < CODE_TTL_MS) {
            setError("");
            setNotice(`Enter the code we sent to ${form.email}.`);
            setStep("confirm");
            return;
        }
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError("");
        setNotice("");
        try {
            const preRes = await preRegister(form.email);
            if (preRes.confirmation === "exists") {
                setError("An account with this email already exists. Please log in.");
            } else if (preRes.confirmation === "success") {
                setLastSent({ email: form.email, at: Date.now() });
                setStep("confirm");
            } else {
                setError("Registration failed. Please try again.");
            }
        } catch (err) {
            setError(errorMessage(err, "Registration failed. Please try again."));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    function sameAddress(a, b) {
        return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
    }

    const handleConfirm = async () => {
        if (inFlight.current) return;
        if (!confirmationCode) { setError("Please enter the confirmation code."); return; }
        inFlight.current = true;
        setLoading(true);
        setError("");
        try {
            const res = await register(form.firstName, form.lastName, form.email, form.password, confirmationCode, inviteToken, getSignupAttribution());
            clearSignupAttribution();
            localStorage.setItem(ftAccessToken, JSON.stringify(res.access_token));
            localStorage.setItem(ftRefreshToken, JSON.stringify(res.refresh_token));
            navigate("/onboard");
        } catch (err) {
            // CONFIRMATION_INVALID carries a message saying what to do
            const codeMessage = err?.code === "CONFIRMATION_INVALID" ? err.body?.message : null;
            // Anything else (network, server error) says nothing about the code, and a
            // registration that did go through has already used it up
            setError(codeMessage || errorMessage(err, "Something went wrong creating your account. Try logging in, or request a new code if that doesn't work."));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError("");
        setNotice("");
        try {
            const preRes = await preRegister(form.email);
            if (preRes.confirmation === "success") {
                setLastSent({ email: form.email, at: Date.now() });
                setConfirmationCode("");
                setNotice(RESENT);
            } else if (preRes.confirmation === "exists") {
                setError("An account with this email already exists. Please log in.");
            } else {
                setError("Could not send a new code. Please try again.");
            }
        } catch (err) {
            setError(errorMessage(err, "Could not send a new code. Please try again."));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    return (
        <div className="auth-layout">
            {/* Left panel */}
            <div className="auth-panel" style={{ backgroundImage: `url(${CoralReef})` }}>
                <div className="auth-panel-overlay" />
                <NetworkCanvas />
                <div className="auth-panel-content">
                    <div className="auth-logo-wrap">
                        <img src={Logo} alt="Logo" className="auth-logo" />
                    </div>
                    <h2 className="auth-panel-title">DeepThought</h2>
                    <p className="auth-panel-sub">Research analytics for the modern scientist.</p>
                </div>
            </div>

            {/* Right form */}
            <div className="auth-form-side">
                <NetworkCanvas dark />
                <div className="auth-form-wrap">

                    {step === "confirm" ? (
                        <>
                            <h1 className="auth-heading">Check your email</h1>
                            <p className="auth-sub">We've emailed a confirmation code to <strong>{form.email}</strong>. Use the newest one; codes work for an hour after they're sent. Check your spam folder if you don't see it.</p>
                            <div className="auth-fields">
                                <div className="auth-field">
                                    <label className="upload-label">Confirmation Code</label>
                                    <input
                                        className="upload-input"
                                        type="text"
                                        placeholder="XXXXX-XXXXX"
                                        autoCapitalize="characters"
                                        autoComplete="one-time-code"
                                        spellCheck={false}
                                        maxLength={20}
                                        value={confirmationCode}
                                        onChange={e => { setConfirmationCode(e.target.value); setError(""); }}
                                        onKeyDown={e => e.key === "Enter" && !e.repeat && handleConfirm()}
                                    />
                                </div>
                            </div>
                            {error && <p className="chpw-error">{error}</p>}
                            {notice && !error && <p className="auth-sub">{notice}</p>}
                            <button className="auth-submit-btn" onClick={handleConfirm} disabled={loading}>
                                {loading ? "Confirming..." : "Confirm"}
                            </button>
                            <p className="auth-switch">
                                Didn't get it, or it expired?{" "}
                                <button className="auth-link auth-link-bold" onClick={handleResend} disabled={loading}>
                                    Send a new code
                                </button>
                            </p>
                            <p className="auth-switch">
                                <button className="auth-link auth-link-bold" onClick={() => { setStep("register"); setError(""); setNotice(""); setConfirmationCode(""); }}>
                                    Back to registration
                                </button>
                            </p>
                        </>
                    ) : (
                    <>
                    {inviteToken && (
                        <div className="pr-invite-banner">
                            <p className="pr-invite-banner-text">You've been invited to review a manuscript on DeepThought. Create an account to get started.</p>
                        </div>
                    )}
                    <h1 className="auth-heading">Create an account</h1>
                    <p className="auth-sub">{inviteToken ? "Sign up to access your review assignment." : "Start analyzing your research papers today."}</p>

                    <div className="auth-fields">
                        <div className="auth-row-2">
                            <div className="auth-field">
                                <label className="upload-label">First Name</label>
                                <input
                                    className="upload-input"
                                    type="text"
                                    name="firstName"
                                    placeholder="Forest"
                                    value={form.firstName}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="auth-field">
                                <label className="upload-label">Last Name</label>
                                <input
                                    className="upload-input"
                                    type="text"
                                    name="lastName"
                                    placeholder="Rohwer"
                                    value={form.lastName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="auth-field">
                            <label className="upload-label">Email</label>
                            <input
                                className="upload-input"
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="auth-field">
                            <label className="upload-label">Password</label>
                            <div className="auth-input-wrap">
                                <input
                                    className="upload-input"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="••••••••"
                                    value={form.password}
                                    onChange={handleChange}
                                />
                                <button
                                    className="auth-eye-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                        </div>

                        <div className="auth-field">
                            <label className="upload-label">Confirm Password</label>
                            <div className="auth-input-wrap">
                                <input
                                    className="upload-input"
                                    type={showConfirm ? "text" : "password"}
                                    name="confirm"
                                    placeholder="••••••••"
                                    value={form.confirm}
                                    onChange={handleChange}
                                />
                                <button
                                    className="auth-eye-btn"
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    tabIndex={-1}
                                >
                                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <label className="auth-agree-row">
                        <input
                            type="checkbox"
                            className="auth-agree-checkbox"
                            checked={agreed}
                            onChange={e => { setAgreed(e.target.checked); setError(""); }}
                        />
                        <span className="auth-agree-text">
                            I agree to the{" "}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="auth-link auth-link-bold">Terms of Service</a>
                            {" "}and{" "}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="auth-link auth-link-bold">Privacy Policy</a>
                        </span>
                    </label>

                    {error && <p className="chpw-error">{error}</p>}

                    <button className="auth-submit-btn" onClick={handleRegister} disabled={loading}>
                        {loading ? "Creating account..." : "Create Account"}
                    </button>

                    <div className="auth-divider">
                        <span>or</span>
                    </div>

                    <button className="auth-google-btn" onClick={() => { if (!agreed) { setError("You must agree to the Terms of Service and Privacy Policy."); return; } handleGoogleSignIn(); }} disabled={!googleReady || loading}>
                        <svg width="16" height="16" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8H6.3C9.7 35.7 16.3 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.4 5.6l6.2 5.2C36.9 40.2 44 35 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                        </svg>
                        Sign up with Google
                    </button>

                    <p className="auth-switch">
                        Already have an account?{" "}
                        <button className="auth-link auth-link-bold" onClick={() => navigate("/login")}>
                            Log in
                        </button>
                    </p>
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}
